import { ArrowLeft, Database, Plus, Trash2, Wand2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, ConfirmDialog, cx, Empty, ErrorNote, Spinner, toast } from "../../components/ui";
import { api, useApi } from "../../lib/api";
import { when } from "./types";

type ParamType = "text" | "number" | "date";
interface Param {
  name: string;
  label: string;
  type: ParamType;
  required: boolean;
  default?: string;
}
interface Entry {
  id: string;
  name: string;
  description: string;
  product: "HCM" | "FSM";
  current: number;
  version: number;
  versions: { version: number; at: string; by: string; note?: string }[];
  sql: string;
  params: Param[];
  canEdit: boolean;
}

/** The SQL library (design §15.9.2): Compass SQL statements that JA Compass SQL steps run, with typed parameters. */
export function SqlLibraryList() {
  const { data, error, loading } = useApi<{ entries: { id: string; name: string; description: string; current: number; updatedAt: string; params: Param[] }[]; canEdit: boolean }>("/apps/radar/sql");
  return (
    <div>
      <Link to="/apps/radar" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mb-3">
        <ArrowLeft className="size-4" aria-hidden /> RADAR
      </Link>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-ink">SQL library</h1>
          <p className="text-sm text-muted">Compass SQL that Job Automation's Compass SQL steps run. Parameters are filled from each run's prompts.</p>
        </div>
        {data?.canEdit && (
          <Link to="/apps/radar/sql/new" className="inline-flex items-center gap-1.5 rounded-lg bg-accent text-white px-3 h-9 text-sm font-medium hover:opacity-90">
            <Plus className="size-4" aria-hidden /> New statement
          </Link>
        )}
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : !data?.entries.length ? (
        <Empty icon={<Database className="size-8" />} title="No SQL statements yet">
          Add the Compass SQL your automations need, with a parameter for each value a run supplies (check dates, period end dates…).
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Statement</th>
                <th className="px-4 py-2 font-medium">Parameters</th>
                <th className="px-4 py-2 font-medium">Version</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} className="border-b border-line last:border-0 align-top">
                  <td className="px-4 py-3">
                    <Link to={`/apps/radar/sql/${e.id}`} className="font-medium text-accent-text hover:underline">
                      {e.name}
                    </Link>
                    <div className="text-xs text-muted">{e.description}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-2">{e.params.map((p) => `${p.label} (${p.type})`).join(", ") || "None"}</td>
                  <td className="px-4 py-3 text-xs text-muted">
                    v{e.current}
                    <div>{when(e.updatedAt)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const PLACEHOLDER = /\{\{param:([A-Za-z][A-Za-z0-9_]{0,40})\}\}/g;
const blank: Entry = { id: "", name: "", description: "", product: "HCM", current: 0, version: 0, versions: [], sql: "", params: [], canEdit: true };

export function SqlLibraryEntry() {
  const { id = "new" } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [entry, setEntry] = useState<Entry | null>(isNew ? blank : null);
  const [form, setForm] = useState<Entry>(blank);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const load = async (version?: number) => {
    try {
      const e = await api<Entry>(`/apps/radar/sql/${id}${version ? `?version=${version}` : ""}`);
      setEntry(e);
      setForm(e);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => {
    if (!isNew) void load();
  }, [id]);
  if (!entry) return error ? <ErrorNote>{error}</ErrorNote> : <Spinner />;
  const viewingOld = !isNew && entry.version !== entry.current;
  const readOnly = !entry.canEdit;
  const set = (patch: Partial<Entry>) => setForm({ ...form, ...patch });
  const setParam = (i: number, patch: Partial<Param>) => set({ params: form.params.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  // Adds a parameter for every {{param:x}} in the SQL that isn't declared yet.
  const detect = () => {
    const names = [...new Set([...form.sql.matchAll(PLACEHOLDER)].map((m) => m[1]))].filter((n) => !form.params.some((p) => p.name === n));
    set({ params: [...form.params, ...names.map((n): Param => ({ name: n, label: n.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase()), type: /date/i.test(n) ? "date" : "text", required: true }))] });
    if (!names.length) toast("No new parameters in the SQL");
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { name: form.name, description: form.description, product: form.product, sql: form.sql, params: form.params, note: note || (viewingOld ? `Restored version ${entry.version}` : undefined), ...(isNew ? {} : { expect: entry.current }) };
      const saved = isNew ? await api<Entry>("/apps/radar/sql", { body }) : await api<Entry>(`/apps/radar/sql/${id}`, { method: "PUT", body });
      toast(isNew ? "Statement added" : `Saved as version ${saved.current}`);
      setNote("");
      if (isNew) navigate(`/apps/radar/sql/${saved.id}`, { replace: true });
      else {
        setEntry(saved);
        setForm(saved);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
  const small = "h-8 rounded-lg border border-line bg-surface px-2 text-sm";

  return (
    <div className="space-y-5">
      <Link to="/apps/radar/sql" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline">
        <ArrowLeft className="size-4" aria-hidden /> SQL library
      </Link>
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold text-ink">{isNew ? "New SQL statement" : entry.name}</h1>
        {!isNew && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">Version</span>
            <select className={small} value={entry.version} onChange={(e) => void load(Number(e.target.value))}>
              {[...entry.versions].reverse().map((v) => (
                <option key={v.version} value={v.version}>
                  v{v.version}
                  {v.version === entry.current ? " (current)" : ""} · {when(v.at)} · {v.by}
                  {v.note ? ` · ${v.note}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {viewingOld && <p className="rounded-lg bg-warn-soft text-ink-2 text-sm px-3 py-2">You're looking at version {entry.version}. Saving makes it the newest version (automations pinned to an older version keep using it until a Designer updates them).</p>}
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <label className="md:col-span-2">
          <span className="text-ink-2 font-medium">Name</span>
          <input className={input} value={form.name} disabled={readOnly} onChange={(e) => set({ name: e.target.value })} maxLength={80} />
        </label>
        <label>
          <span className="text-ink-2 font-medium">Product line</span>
          <select className={input} value={form.product} disabled={readOnly} onChange={(e) => set({ product: e.target.value as Entry["product"] })}>
            <option value="HCM">HCM</option>
            <option value="FSM">FSM</option>
          </select>
        </label>
        <label className="md:col-span-3">
          <span className="text-ink-2 font-medium">Description</span>
          <input className={input} value={form.description} disabled={readOnly} onChange={(e) => set({ description: e.target.value })} maxLength={500} placeholder="What it returns and which report reads it" />
        </label>
      </div>
      <label className="block text-sm">
        <span className="text-ink-2 font-medium">SQL</span>
        <textarea className="w-full h-96 rounded-lg border border-line bg-surface p-3 font-mono text-xs leading-relaxed" value={form.sql} disabled={readOnly} spellCheck={false} onChange={(e) => set({ sql: e.target.value })} placeholder="WITH Params AS (SELECT '{{param:hrOrganization}}' AS HROrganization, '{{param:checkDate}}' AS CheckDate) …" />
        <span className="block text-xs text-muted">Write each value a run supplies as {"{{param:name}}"}. Text and dates go inside quotes ('{"{{param:checkDate}}"}'); numbers may stand alone. Values are inserted safely, so they can't change the query.</span>
      </label>
      <section className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">Parameters</h2>
          {!readOnly && (
            <div className="flex gap-2">
              <Button onClick={detect}>
                <Wand2 className="size-4" aria-hidden /> Find in the SQL
              </Button>
              <Button onClick={() => set({ params: [...form.params, { name: "", label: "", type: "text", required: true }] })}>
                <Plus className="size-4" aria-hidden /> Add
              </Button>
            </div>
          )}
        </div>
        {form.params.length === 0 ? (
          <p className="text-muted">No parameters.</p>
        ) : (
          <table className="w-full">
            <thead className="text-left text-muted text-xs">
              <tr>
                <th className="py-1 pr-2 font-medium">Name in the SQL</th>
                <th className="py-1 pr-2 font-medium">Label (shown in the Designer)</th>
                <th className="py-1 pr-2 font-medium">Type</th>
                <th className="py-1 pr-2 font-medium">Required</th>
                <th className="py-1 pr-2 font-medium">Default</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {form.params.map((p, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2">
                    <input className={cx(small, "w-full font-mono")} value={p.name} disabled={readOnly} onChange={(e) => setParam(i, { name: e.target.value.trim() })} />
                  </td>
                  <td className="py-1 pr-2">
                    <input className={cx(small, "w-full")} value={p.label} disabled={readOnly} onChange={(e) => setParam(i, { label: e.target.value })} />
                  </td>
                  <td className="py-1 pr-2">
                    <select className={small} value={p.type} disabled={readOnly} onChange={(e) => setParam(i, { type: e.target.value as ParamType })}>
                      <option value="text">Text</option>
                      <option value="date">Date</option>
                      <option value="number">Number</option>
                    </select>
                  </td>
                  <td className="py-1 pr-2 text-center">
                    <input type="checkbox" checked={p.required} disabled={readOnly} onChange={(e) => setParam(i, { required: e.target.checked })} />
                  </td>
                  <td className="py-1 pr-2">
                    <input className={cx(small, "w-full")} value={p.default ?? ""} disabled={readOnly} onChange={(e) => setParam(i, { default: e.target.value || undefined })} placeholder={p.type === "date" ? "YYYY-MM-DD" : ""} />
                  </td>
                  <td className="py-1">
                    {!readOnly && (
                      <Button variant="ghost" aria-label="Remove" onClick={() => set({ params: form.params.filter((_, j) => j !== i) })}>
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {!readOnly && (
        <div className="flex items-center gap-2">
          {!isNew && <input className={cx(input, "max-w-sm")} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed (optional)" maxLength={200} />}
          <Button variant="primary" busy={busy} disabled={!form.name.trim() || !form.sql.trim()} onClick={() => void save()}>
            {isNew ? "Add statement" : "Save as a new version"}
          </Button>
          {!isNew && (
            <Button variant="ghost" className="ml-auto text-bad" onClick={() => setDeleting(true)}>
              <Trash2 className="size-4" aria-hidden /> Remove from the library
            </Button>
          )}
        </div>
      )}
      {deleting && (
        <ConfirmDialog
          title={`Remove ${entry.name}?`}
          confirm="Remove"
          danger
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await api(`/apps/radar/sql/${id}`, { method: "DELETE" });
            toast("Removed from the library");
            navigate("/apps/radar/sql");
          }}
        >
          It disappears from the library and the Designer's list. Automations already pinned to one of its versions keep running it.
        </ConfirmDialog>
      )}
    </div>
  );
}
